import { useI18n } from '@runtime/core/i18n/i18n'
import { useEffect, useRef } from 'react'
import { trapDialogFocus } from '@/shared/a11y/trapDialogFocus'
import styles from './PlayMobileMenu.module.scss'
import PlayNavigation from './PlayNavigation'
import PlayRailUtilities from './PlayRailUtilities'
import { usePlayMenu } from './playMenuContext'
import { useRailProfileFocus } from './useRailProfileFocus'

export default function PlayMobileMenu({
  open,
  immersive = false,
  onClose,
}: {
  open: boolean
  immersive?: boolean
  onClose: () => void
}) {
  const { t } = useI18n()
  const menu = usePlayMenu()
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const media = window.matchMedia('(min-width: 1024px)')
    const closeOnDesktop = () => {
      if (media.matches && !immersive) {
        // Release modal inertness before the shell focuses its desktop return target.
        ref.current?.close()
        onClose()
      }
    }
    media.addEventListener('change', closeOnDesktop)
    return () => {
      document.body.style.overflow = previousOverflow
      media.removeEventListener('change', closeOnDesktop)
    }
  }, [open, immersive, onClose])
  const close = () => {
    ref.current?.close()
    onClose()
  }
  const openProfile = useRailProfileFocus(close)
  return (
    <dialog
      ref={ref}
      id="play-mobile-menu"
      className={styles.dialog}
      aria-label={t('common.menu')}
      data-testid="play-mobile-menu"
      onCancel={event => {
        event.preventDefault()
        close()
      }}
      onKeyDown={event => {
        trapDialogFocus(event, ref.current)
        if (event.key === 'Escape' && !event.nativeEvent.isComposing) {
          event.preventDefault()
          event.stopPropagation()
          close()
        }
      }}
      onClose={onClose}
      onClick={event => {
        if (event.target !== event.currentTarget) return
        const bounds = event.currentTarget.getBoundingClientRect()
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          close()
      }}
    >
      {open && (
        <div className={styles.panel}>
          <header className={styles.heading}>
            <button
              type="button"
              className={styles.close}
              onClick={close}
              aria-label={t('common.playNavigation.closeMenu')}
              data-testid="play-menu-close"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </header>
          {menu?.beforeNavigate && (
            <button
              type="button"
              className={styles.return}
              aria-label={t('common.playNavigation.returnToGameMenu')}
              data-testid="play-menu-return"
              onClick={() => {
                if ((menu.returnToMenu ?? menu.beforeNavigate)?.() !== false) close()
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="m10 5-7 7 7 7M3 12h18" />
              </svg>
              <span>{t('common.back')}</span>
            </button>
          )}
          <PlayNavigation mobile onNavigate={close} />
          {immersive && <PlayRailUtilities onAction={openProfile} />}
        </div>
      )}
    </dialog>
  )
}
